// "Which projects do I operate, and which of them are environments of which?"
//
// A dev/staging environment on s3p is not a mode inside a project — it IS a
// project, a sibling slug with its own bucket, pool, roles and runtime (see
// environmentSlug in the SDK's conventions). That makes the console's job here
// purely presentational: group the flat list useOperableProjects already
// returns, so `todo-dev` renders under `todo` instead of beside it.
//
// NO EXTRA FETCHES. The grouping key rides on manifests the list already
// loaded, because the link is stored on the CHILD (`environmentOf`) rather than
// as an array on the base. That choice also decides visibility for free: the
// list is filtered by operator group, so an environment you can't operate is
// simply absent, where a base-side array would have rendered a row that opens
// to an AccessDenied.
//
// The grouping itself is `groupByEnvironment` in the SDK — pure, shared, and
// unit-tested there (this app has no test harness, and the orphan-child case
// is exactly the kind of thing that needs one).

import { environmentCandidates, groupByEnvironment } from "/sdk/index.js";
import { useMemo } from "react";
import type { ProjectManifest } from "../lib/contracts.ts";
import { useOperableProjects } from "./useOperableProjects.ts";

export interface EnvCandidate {
	slug: string;
	base: string;
	environment: string;
}

export interface ProjectTreeState {
	data: ProjectManifest[] | null;
	loading: boolean;
	error: string | null;
	reload: () => void;
	roots: ProjectManifest[];
	envs: Map<string, ProjectManifest[]>;
	/** Standalone projects NAMED like an environment of a base you also
	 *  operate. Keyed by slug so a card can ask about itself. */
	candidates: Map<string, EnvCandidate>;
}

export function useProjectTree(): ProjectTreeState {
	const projects = useOperableProjects();
	const tree = useMemo(() => groupByEnvironment(projects.data ?? []), [projects.data]);
	const candidates = useMemo(
		() =>
			new Map(
				environmentCandidates(projects.data ?? []).map((c) => [c.slug, c] as const),
			),
		[projects.data],
	);
	return { ...projects, ...tree, candidates };
}
